"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Client = void 0;
const axios_1 = require("axios");
const buffer_1 = require("buffer");
const utils_1 = require("./utils");
/**
 * A high-level http client for communicating with the Lucidtech REST API
 */
class Client {
    constructor(credentials) {
        this.credentials = credentials;
    }
    /**
     * Updates an organization, calls the PATCH /organizations/{organizationId} endpoint.
     *
     * @param organizationId Id of the organization
     * @param options.description Description of organization
     * @param options.name Name of organization
     * @returns Organization response from REST API with content
     */
    updateOrganization(organizationId, options) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.makePatchRequest(`/organizations/${organizationId}`, options);
        });
    }
    /**
     * Get organization from the REST API, calls the GET /organizations/{organizationId} endpoint.
     *
     * @param organizationId Id of the organization
     * @returns Organization response from REST API
     */
    getOrganization(organizationId, options) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.makeGetRequest(`/organizations/${organizationId}`, options);
        });
    }
    /**
     * Creates an app client, calls the POST /appClients endpoint.
     *
     * @param options.callbackUrls List of callback urls
     * @param options.description Description of app client
     * @param options.generateSecret Set to true to generate credentials for the client_credentials grant type
     * @param options.logoutUrls List of logout urls
     * @param options.name Name of app client
     * @returns AppClient response from REST API
     */
    createAppClient(options) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.makePostRequest('/appClients', options);
        });
    }
    /**
     * Updates an appClient, calls the PATCH /appClients/{appClientId} endpoint.
     *
     * @param appClientId Id of the appClient
     * @param options.description Description of app client
     * @param options.name Name of app client
     * @returns AppClient response from REST API with content
     */
    updateAppClient(appClientId, options) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.makePatchRequest(`/appClients/${appClientId}`, options);
        });
    }
    /**
     * List app clients, calls the GET /appClients endpoint.
     *
     * @param options.maxResults Maximum number of results to be returned
     * @param options.nextToken A unique token for each page, use the returned token to retrieve the next page.
     * @returns AppClientList response from REST API
     */
    listAppClients(options) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.makeGetRequest('/appClients', options);
        });
    }
    /**
     * Delete the app client, calls the DELETE /appClients/{appClientId} endpoint.
     *
     * @param appClientId of the app client
     * @returns AppClient response from REST API
     */
    deleteAppClient(appClientId, options) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.makeDeleteRequest(`/appClients/${appClientId}`, options);
        });
    }
    /**
     * Creates a document, calls the POST /documents endpoint.
     *
     * @param content Content to POST (Buffer)
     * @param contentType MIME type for the document
     * @param options.consentId Id of the consent that marks the owner of the document
     * @param options.groundTruth List of GroundTruth items representing the ground truth values for the document
     * @param options.description Description of document
     * @param options.name Name of document
     * @returns Document response from REST API
     */
    createDocument(content, contentType, options) {
        return __awaiter(this, void 0, void 0, function* () {
            let body = {
                contentType,
            };
            if (options) {
                body = Object.assign(Object.assign({}, body), options);
            }
            const lasDoc = yield this.makePostRequest('/documents', body);
            const asBuffer = buffer_1.Buffer.from(content);
            yield this.makeFileServerPutRequest(lasDoc.fileUrl, asBuffer);
            // At this point, contentType = null
            // Knowing the fileserver PUT request succeeded, we manually override this
            lasDoc.contentType = contentType;
            return lasDoc;
        });
    }
    /**
     * Get document from the REST API, calls the GET /documents/{documentId} endpoint.
     *
     * @param documentId Id of the document
     * @returns Document response from REST API
     */
    getDocument(documentId, options) {
        return __awaiter(this, void 0, void 0, function* () {
            const lasDocument = yield this.makeGetRequest(`/documents/${documentId}`, options);
            if (!lasDocument.content && lasDocument.fileUrl) {
                const fileServerDocument = yield this.makeFileServerGetRequest(lasDocument.fileUrl);
                lasDocument.content = (0, utils_1.arrayBufferToBase64)(fileServerDocument);
            }
            return lasDocument;
        });
    }
    /**
     * List documents available for inference, calls the GET /documents endpoint.
     *
     * @param options.consentId Ids of the consents that marks the owner of the document
     * @param options.maxResults Maximum number of results to be returned
     * @param options.nextToken A unique token for each page, use the returned token to retrieve the next page.
     * @returns Documents response from REST API
     */
    listDocuments(options) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.makeGetRequest('/documents', options);
        });
    }
    /**
     * Post ground truth to the REST API, calls the PATCH /documents/{documentId} endpoint.
     * Posting ground truth means posting the ground truth data for the particular document.
     * This enables the API to learn from past mistakes.
     *
     * @param documentId Id of the document
     * @param data.groundTruth List of GroundTruth items representing the ground truth values for the document
     * @param data.description Description of document
     * @param data.name Name of document
     * @returns Document response from REST API
     */
    updateDocument(documentId, data) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.makePatchRequest(`/documents/${documentId}`, data);
        });
    }
    /**
     * Delete documents with the provided consentId, calls the DELETE /documents endpoint.
     * Will delete all documents when no consentId is provided.
     *
     * @param options.consentId Ids of the consents that marks the owner of the document
     * @returns Documents response from REST API
     */
    deleteDocuments(options) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.makeDeleteRequest('/documents', options);
        });
    }
    /**
     * Delete an document, calls the DELETE /documents/{documentId} endpoint.
     *
     * @param documentId of the document
     * @returns Document response from REST API
     */
    deleteDocument(documentId, options) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.makeDeleteRequest(`/documents/${documentId}`, options);
        });
    }
    /**
     * Creates a transition, calls the POST /transitions endpoint.
     *
     * @param name Name of transition
     * @param transitionType Type of transition "docker"|"manual"
     * @param options.inputJsonSchema Json-schema that defines the input to the transition
     * @param options.outputJsonSchema Json-schema that defines the output of the transition
     * @param options.description Description of the transition
     * @param options.params Extra parameters to the transition
     * @returns Transition response from REST API
     */
    createTransition(transitionType, options) {
        return __awaiter(this, void 0, void 0, function* () {
            let body = {
                transitionType,
            };
            if (options) {
                body = Object.assign(Object.assign({}, body), options);
            }
            return this.makePostRequest('/transitions', body);
        });
    }
    /**
     * Get the transition with the provided transitionId, calls the GET /transitions/{transitionId} endpoint.
     *
     * @param transitionId Id of the transition
     * @returns Transition response from REST API
     */
    getTransition(transitionId, options) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.makeGetRequest(`/transitions/${transitionId}`, options);
        });
    }
    /**
     * List transitions, calls the GET /transitions endpoint.
     *
     * @param options.transitionType Types of transitions
     * @param options.maxResults Maximum number of results to be returned
     * @param options.nextToken A unique token for each page, use the returned token to retrieve the next page.
     * @returns Transitions response from REST API
     */
    listTransitions(options) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.makeGetRequest('/transitions', options);
        });
    }
    /**
     * Updates a transition, calls the PATCH /transitions/{transitionId} endpoint.
     *
     * @param transitionId Id of the transition
     * @param data Transition fields to PATCH
     * @returns Transition response from REST API
     */
    updateTransition(transitionId, data) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.makePatchRequest(`/transitions/${transitionId}`, data);
        });
    }
    /**
     * Delete the transition with the provided transitionId, calls the DELETE /transitions/{transitionId} endpoint.
     * Will fail if transition is in use by one or more workflows.
     *
     * @param transitionId Id of the transition
     * @returns Transition response from REST API
     */
    deleteTransition(transitionId, options) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.makeDeleteRequest(`/transitions/${transitionId}`, options);
        });
    }
    /**
     * Start executing a manual transition, calls the POST /transitions/{transitionId}/executions endpoint.
     *
     * @param transitionId Id of the transition
     * @returns Transition execution response from REST API
     */
    executeTransition(transitionId, options) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.makePostRequest(`/transitions/${transitionId}/executions`, options);
        });
    }
    /**
     * Get an execution of a transition, calls the GET /transitions/{transitionId}/executions/{executionId} endpoint
     *
     * @param transitionId Id of the transition
     * @param transitionExecutionId Id of the execution
     * @returns Transition execution responses from REST API
     */
    getTransitionExecution(transitionId, transitionExecutionId, options) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.makeGetRequest(`/transitions/${transitionId}/executions/${transitionExecutionId}`, options);
        });
    }
    /**
     * Ends the processing of the transition execution, calls the
     * PATCH /transitions/{transitionId}/executions/{executionId} endpoint.
     *
     * @param transitionId Id of the transition that performs the execution
     * @param executionId Id of the execution to update
     * @param data.status Status of the execution 'succeeded|failed'
     * @param data.output Output from the execution, required when status is 'succeded'
     * @param data.error Error from the execution, required when status is 'failed', needs to contain 'message'
     * @param data.startTime Utc start time that will replace the original start time of the execution
     * @returns Transition execution response from REST API
     */
    updateTransitionExecution(transitionId, executionId, data) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.makePatchRequest(`/transitions/${transitionId}/executions/${executionId}`, data);
        });
    }
    /**
     * List executions in a transition, calls the GET /transitions/{transitionId}/executions endpoint.
     *
     * @param transitionId Id of the transition
     * @param options.status Statuses of the executions
     * @param options.executionId Ids of the executions
     * @param options.maxResults Maximum number of results to be returned
     * @param options.nextToken A unique token for each page, use the returned token to retrieve the next page.
     * @returns Transition executions responses from REST API
     */
    listTransitionExecutions(transitionId, options) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.makeGetRequest(`/transitions/${transitionId}/executions`, options);
        });
    }
    /**
     * Send heartbeat for a manual execution to signal that we are still working on it.
     * Must be done at minimum once every 60 seconds or the transition execution will time out.
     * Calls the POST /transitions/{transitionId}/executions/{executionId}/heartbeats endpoint.
     *
     * @param transitionId Id of the transition
     * @param transitionExecutionId Id of the transition execution
     * @returns Empty response
     */
    sendHeartbeat(transitionId, transitionExecutionId, options) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.makePostRequest(`/transitions/${transitionId}/executions/${transitionExecutionId}/heartbeats`, options);
        });
    }
    /**
     * Creates a new workflow, calls the POST /workflows endpoint.
     *
     * @param name Name of the workflow
     * @param specification Specification of the workflow
     * @param options.description Description of the workflow
     * @param options.errorConfig Configuration of error handler
     * @returns Workflow response from REST API
     */
    createWorkflow(name, specification, options) {
        return __awaiter(this, void 0, void 0, function* () {
            let body = {
                name,
                specification,
            };
            if (options) {
                body = Object.assign(Object.assign({}, body), options);
            }
            return this.makePostRequest('/workflows', body);
        });
    }
    /**
     * Get the workflow with the provided workflowId, calls the GET /workflows/{workflowId} endpoint.
     *
     * @param workflowId Id of the workflow
     * @returns Workflow response from REST API
     */
    getWorkflow(workflowId, options) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.makeGetRequest(`/workflows/${workflowId}`, options);
        });
    }
    /**
     * List workflows, calls the GET /workflows endpoint.
     *
     * @param options.maxResults Maximum number of results to be returned
     * @param options.nextToken A unique token for each page, use the returned token to retrieve the next page.
     * @returns Workflows response from REST API
     */
    listWorkflows(options) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.makeGetRequest('/workflows', options);
        });
    }
    /**
     * Delete the workflow with the provided workflowId, calls the DELETE /workflows/{workflowId} endpoint.
     *
     * @param workflowId Id of the workflow
     * @returns Workflow response from REST API
     */
    deleteWorkflow(workflowId, options) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.makeDeleteRequest(`/workflows/${workflowId}`, options);
        });
    }
    /**
     * Updates a workflow, calls the PATCH /workflows/{workflowId} endpoint.
     * @param workflowId Id of the workflow
     * @param data Workflow fields to PATCH
     * @returns Workflow response from REST API
     */
    updateWorkflow(workflowId, data) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.makePatchRequest(`/workflows/${workflowId}`, data);
        });
    }
    /**
     * Start a workflow execution, calls the POST /workflows/{workflowId}/executions endpoint.
     *
     * @param workflowId Id of the workflow
     * @param input Input to the first step of the workflow
     * @returns Workflow execution response from REST API
     */
    executeWorkflow(workflowId, input, options) {
        return __awaiter(this, void 0, void 0, function* () {
            const body = Object.assign({ input }, options);
            return this.makePostRequest(`/workflows/${workflowId}/executions`, body);
        });
    }
    /**
     * List executions in a workflow, calls the GET /workflows/{workflowId}/executions endpoint.
     *
     * @param workflowId Id of the workflow
     * @param options.status Statuses of the executions
     * @param options.maxResults Maximum number of results to be returned
     * @param options.nextToken A unique token for each page, use the returned token to retrieve the next page.
     * @param options.sortBy What field to sort by ('startTime' | 'endTime')
     * @param options.order Sort order ('ascending' | 'descending')
     * @returns Workflow executions responses from REST API
     */
    listWorkflowExecutions(workflowId, options) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.makeGetRequest(`/workflows/${workflowId}/executions`, options);
        });
    }
    /**
     * Get a workflow execution, calls the GET /workflows/{workflowId}/executions/{executionId} endpoint.
     *
     * @param workflowId Id of the workflow that performs the execution
     * @param executionId Id of the execution to get
     * @returns Workflow execution response from REST API
     */
    getWorkflowExecution(workflowId, executionId, options) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.makeGetRequest(`/workflows/${workflowId}/executions/${executionId}`, options);
        });
    }
    /**
     * Retry or end the processing of a workflow execution,
     * calls the PATCH /workflows/{workflowId}/executions/{executionId} endpoint.
     *
     * @param workflowId Id of the workflow that performs the execution
     * @param executionId Id of the execution to update
     * @param data.nextTransitionId The next transition to transition into. To end the workflow-execution,
     *  use: las:transition:commons-failed.
     * @returns Workflow execution response from REST API
     */
    updateWorkflowExecution(workflowId, executionId, data) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.makePatchRequest(`/workflows/${workflowId}/executions/${executionId}`, data);
        });
    }
    /**
     * Deletes the execution with the provided executionId from workflowId,
     * calls the DELETE /workflows/{workflowId}/executions/{executionId} endpoint.
     *
     * @param workflowId Id of the workflow
     * @param executionId Id of the execution
     * @returns WorkflowExecution response from REST API
     */
    deleteWorkflowExecution(workflowId, executionId, options) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.makeDeleteRequest(`/workflows/${workflowId}/executions/${executionId}`, options);
        });
    }
    /**
     * Create a prediction on a document using specified model, calls the POST /predictions endpoint.
     *
     * @param documentId Id of the document to run inference and create a prediction on
     * @param modelId Id of the model to use for inference
     * @param options.maxPages Maximum number of pages to run predictions on
     * @param options.autoRotate Whether or not to let the API try different rotations on the document
     * when running predictions
     * @param options.imageQuality: Image quality for prediction ("LOW|HIGH"). High quality could give
     * better result but will also take longer time.
     * @returns Predicion response from REST API
     */
    createPrediction(documentId, modelId, options) {
        return __awaiter(this, void 0, void 0, function* () {
            let body = {
                documentId,
                modelId,
            };
            if (options) {
                body = Object.assign(Object.assign({}, body), options);
            }
            return this.makePostRequest('/predictions', body);
        });
    }
    listPredictions(options) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.makeGetRequest('/predictions', options);
        });
    }
    /**
     * Creates an asset, calls the POST /assets endpoint.
     *
     * @param content Content to POST (base64-encoded string | Buffer)
     * @returns Asset response from REST API
     */
    createAsset(content, options) {
        return __awaiter(this, void 0, void 0, function* () {
            const encodedContent = typeof content === 'string' ? content : buffer_1.Buffer.from(content).toString('base64');
            return this.makePostRequest('/assets', Object.assign({ content: encodedContent }, options));
        });
    }
    /**
     * Delete an asset, calls the DELETE /assets/{assetId} endpoint.
     *
     * @param assetId of the app client
     * @returns Asset response from REST API
     */
    deleteAsset(assetId, options) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.makeDeleteRequest(`/assets/${assetId}`, options);
        });
    }
    /**
     * List assets available, calls the GET /assets endpoint.
     *
     * @param options.maxResults Maximum number of results to be returned
     * @param options.nextToken A unique token for each page, use the returned token to retrieve the next page.
     * @returns Assets response from REST API without the content of each asset
     */
    listAssets(options) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.makeGetRequest('/assets', options);
        });
    }
    /**
     * Get asset from the REST API, calls the GET /assets/{assetId} endpoint.
     *
     * @param assetId Id of the asset
     * @returns Asset response from REST API
     */
    getAsset(assetId, options) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.makeGetRequest(`/assets/${assetId}`, options);
        });
    }
    /**
     * Updates an asset, calls the PATCH /assets/{assetId} endpoint.
     *
     * @param assetId Id of the asset
     * @param data.content Content to PATCH (base64-encoded string | Buffer)
     * @returns Asset response from REST API with content
     */
    updateAsset(assetId, data) {
        return __awaiter(this, void 0, void 0, function* () {
            let body;
            if (data) {
                body = Object.assign({}, data);
                if (data.content) {
                    const encodedContent = typeof data.content === 'string' ? data.content : buffer_1.Buffer.from(data.content).toString('base64');
                    body = Object.assign(Object.assign({}, body), { content: encodedContent });
                }
            }
            return this.makePatchRequest(`/assets/${assetId}`, body);
        });
    }
    /**
     * Get dataset from the REST API, calls the GET /datasets/{datasetId} endpoint.
     *
     * @param datasetId Id of the dataset
     * @returns Dataset response from REST API
     */
    getDataset(datasetId, options) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.makeGetRequest(`/datasets/${datasetId}`, options);
        });
    }
    /**
     * Creates a dataset, calls the POST /datasets endpoint.
     *
     * @param options.name Name of the dataset
     * @param options.description Description of the dataset
     * @param options.retentionInDays Maximum retention for documents in the dataset
     * @param options.containsPersonallyIdentifiableInformation If dataset contains personally identifiable information
     * @returns Dataset response from REST API
     */
    createDataset(options) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.makePostRequest('/datasets', options);
        });
    }
    /**
     * Updates a dataset, calls the PATCH /datasets/{datasetId} endpoint.
     *
     * @param datasetId Id of the dataset
     * @param options.description Description of dataset
     * @param options.name Name of dataset
     * @param options.retentionInDays Maximum retention for documents in the dataset
     * @param options.containsPersonallyIdentifiableInformation If dataset contains personally identifiable information
     * @returns Dataset response from REST API with content
     */
    updateDataset(datasetId, options) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.makePatchRequest(`/datasets/${datasetId}`, options);
        });
    }
    /**
     * List datasets, calls the GET /datasets endpoint.
     *
     * @param options.maxResults Maximum number of results to be returned
     * @param options.nextToken A unique token for each page, use the returned token to retrieve the next page.
     * @returns DatasetList response from REST API
     */
    listDatasets(options) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.makeGetRequest('/datasets', options);
        });
    }
    /**
     * Deletes a dataset, calls the DELETE /datasets/{datasetId} endpoint.
     *
     * @param datasetId Id of the dataset
     * @param deleteDocuments Set to true to delete documents in dataset before deleting dataset
     * @returns Dataset response from REST API
     */
    deleteDataset(datasetId, deleteDocuments = false, options) {
        return __awaiter(this, void 0, void 0, function* () {
            if (deleteDocuments) {
                let response = yield this.deleteDocuments({ datasetId });
                while (response.nextToken) {
                    response = yield this.deleteDocuments({ datasetId, nextToken: response.nextToken });
                }
                let WAIT_MS = 1000;
                let TOTAL_WAIT = WAIT_MS;
                const MAX_WAIT_MS = 10000;
                yield (0, utils_1.wait)(WAIT_MS);
                let datasetResponse = yield this.getDataset(datasetId);
                // wait until we get the updated numberOfDocuments, OR we time out
                while (datasetResponse.numberOfDocuments > 0 && TOTAL_WAIT < MAX_WAIT_MS) {
                    // exponentially back off
                    WAIT_MS *= 1.25;
                    TOTAL_WAIT += WAIT_MS;
                    yield (0, utils_1.wait)(WAIT_MS);
                    datasetResponse = yield this.getDataset(datasetId);
                }
                // if the numberOfDocuments has not yet been updated, throw an error
                if (datasetResponse.numberOfDocuments > 0) {
                    throw Error('Dataset numberOfDocuments not updated in time');
                }
            }
            return this.makeDeleteRequest(`/datasets/${datasetId}`, options);
        });
    }
    /**
     * Creates a dataBundle, calls the POST /models/{modelId}/dataBundles endpoint.
     *
     * @param modelId Id of the model to create dataBundle for
     * @param datasetIds Ids of the datasets to create dataBundle with
     * @param options.description Description of dataBundle
     * @param options.name Name of dataBundle
     * @returns DataBundle response from REST API
     */
    createDataBundle(modelId, datasetIds, options) {
        return __awaiter(this, void 0, void 0, function* () {
            let body = { datasetIds };
            if (options) {
                body = Object.assign(Object.assign({}, body), options);
            }
            return this.makePostRequest(`/models/${modelId}/dataBundles`, body);
        });
    }
    /**
     * Delete a dataBundle, calls the DELETE /dataBundles/{dataBundleId} endpoint.
     *
     * @param modelId of the model
     * @param dataBundleId of the dataBundle
     * @returns DataBundle response from REST API
     */
    deleteDataBundle(modelId, dataBundleId, options) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.makeDeleteRequest(`/models/${modelId}/dataBundles/${dataBundleId}`, options);
        });
    }
    /**
     * List dataBundles available, calls the GET /dataBundles endpoint.
     *
     * @param modelId of the model
     * @param options.maxResults Maximum number of results to be returned
     * @param options.nextToken A unique token for each page, use the returned token to retrieve the next page.
     * @returns DataBundles response from REST API
     */
    listDataBundles(modelId, options) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.makeGetRequest(`/models/${modelId}/dataBundles`, options);
        });
    }
    /**
     * Updates a dataBundle, calls the PATCH /dataBundles/{dataBundleId} endpoint.
     *
     * @param modelId of the model
     * @param dataBundleId Id of the dataBundle
     * @param options.description Description of dataBundle
     * @param options.name Name of dataBundle
     * @returns DataBundle response from REST API
     */
    updateDataBundle(modelId, dataBundleId, options) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.makePatchRequest(`/models/${modelId}/dataBundles/${dataBundleId}`, options);
        });
    }
    /**
     * Creates a new user, calls the POST /users endpoint.
     *
     * @param email Email to the new user
     * @param data.name Name of the user
     * @param data.avatar base64 encoded JPEG avatar of the user
     * @param data.appClientId Id of appClient issuing the invite. Defaults to the current
     * appClientId of the user or appClient issuing the invite
     * @returns User response from REST API
     */
    createUser(email, data) {
        return __awaiter(this, void 0, void 0, function* () {
            let body = { email };
            if (data) {
                body = Object.assign(Object.assign({}, body), data);
            }
            return this.makePostRequest('/users', body);
        });
    }
    /**
     * List users, calls the GET /users endpoint.
     *
     * @param options.maxResults Maximum number of results to be returned
     * @param options.nextToken A unique token for each page, use the returned token to retrieve the next page.
     * @returns User response from REST API
     */
    listUsers(options) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.makeGetRequest('/users', options);
        });
    }
    /**
     * Get information about a specific user, calls the GET /users/{userId} endpoint.
     *
     * @param userId Id of the user
     * @returns User response from REST API
     */
    getUser(userId, options) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.makeGetRequest(`/users/${userId}`, options);
        });
    }
    /**
     * Updates a user, calls the PATCH /users/{userId} endpoint.
     *
     * @param userId Id of the user
     * @param data.name Name of the user
     * @param data.avatar base64 encoded JPEG avatar of the user
     * @returns User response from REST API
     */
    updateUser(userId, data) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.makePatchRequest(`/users/${userId}`, data);
        });
    }
    /**
     * Delete a user, calls the DELETE /users/{userId} endpoint.
     *
     * @param userId Id of the user
     * @returns User response from REST API
     */
    deleteUser(userId, options) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.makeDeleteRequest(`/users/${userId}`, options);
        });
    }
    /**
     * Creates a secret, calls the POST /secrets endpoint.
     *
     * @param data Object containing the data you want to keep secret
     * @param options.description Description of the secret
     * @returns Secret response from REST API
     */
    createSecret(data, options) {
        return __awaiter(this, void 0, void 0, function* () {
            let body = { data };
            if (options) {
                body = Object.assign(Object.assign({}, body), options);
            }
            return this.makePostRequest('/secrets', body);
        });
    }
    /**
     * List secrets available, calls the GET /secrets endpoint.
     *
     * @param options.maxResults Maximum number of results to be returned
     * @param options.nextToken A unique token for each page, use the returned token to retrieve the next page.
     * @returns Secrets response from REST API without the username of each secret
     */
    listSecrets(options) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.makeGetRequest('/secrets', options);
        });
    }
    /**
     * Creates a model, calls the POST /models endpoint.
     *
     * @param fieldConfig Specification of the fields that the model is going to predict
     * @param options.width The number of pixels to be used for the input image width of your model
     * @param options.height The number of pixels to be used for the input image height of your model
     * @param options.description Description of the model
     * @param options.name Name of the model
     * @param options.preprocessConfig Specification of the processing steps prior to the prediction of an image
     * @returns Model response from REST API
     */
    createModel(fieldConfig, options) {
        return __awaiter(this, void 0, void 0, function* () {
            let body = { fieldConfig };
            if (options) {
                body = Object.assign(Object.assign({}, body), options);
            }
            return this.makePostRequest('/models', body);
        });
    }
    /**
     * Get model from the REST API, calls the GET /models/{modelId} endpoint.
     *
     * @param modelId Id of the model
     * @returns Model response from REST API
     */
    getModel(modelId, options) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.makeGetRequest(`/models/${modelId}`, options);
        });
    }
    /**
     * Updates a model, calls the PATCH /models/{modelId} endpoint.
     *
     * @param modelId Id of the model
     * @param options.description Description of the model
     * @param options.fieldConfig Specification of the fields that the model is going to predict
     * @param options.height The number of pixels to be used for the input image height of your model
     * @param options.name Name of the model
     * @param options.preprocessConfig Specification of the processing steps prior to the prediction of an image
     * @param options.status Update status to training
     * @param options.width The number of pixels to be used for the input image width of your model
     * @returns Model response from REST API
     */
    updateModel(modelId, options) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.makePatchRequest(`/models/${modelId}`, options);
        });
    }
    /**
     * Delete an model, calls the DELETE /models/{modelId} endpoint.
     *
     * @param modelId Id of the model
     * @returns Model response from REST API
     */
    deleteModel(modelId, options) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.makeDeleteRequest(`/models/${modelId}`, options);
        });
    }
    /**
     * List models available, calls the GET /models endpoint.
     *
     * @param options.maxResults Maximum number of results to be returned
     * @param options.nextToken A unique token for each page, use the returned token to retrieve the next page.
     * @returns Models response from the REST API
     */
    listModels(options) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.makeGetRequest('/models', options);
        });
    }
    /**
     * List deployment environments available, calls the GET /deploymentEnvironments endpoint.
     *
     * @param options.maxResults Maximum number of results to be returned
     * @param options.nextToken A unique token for each page, use the returned token to retrieve the next page.
     * @param options.owner Organization owner
     * @returns Deployment environments response from the REST API
     */
    listDeploymentEnvironments(options) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.makeGetRequest('/deploymentEnvironments', options);
        });
    }
    /**
     * Get deployment environment from the REST API, calls the GET /deploymentEnvironments/{deploymentEnvironmentId} endpoint.
     * @param deploymentEnvironmentId Id of the deployment environment
     * @returns Deployment environment response from the REST API
     */
    getDeploymentEnvironment(deploymentEnvironmentId, options) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.makeGetRequest(`/deploymentEnvironments/${deploymentEnvironmentId}`, options);
        });
    }
    /**
     * List trainings available, calls the GET /models/{modelId}/trainings endpoint.
     *
     * @param modelId Id of the model
     * @param options.maxResults Maximum number of results to be returned
     * @param options.nextToken A unique token for each page, use the returned token to retrieve the next page.
     * @param options.status List Trainings with the specified TrainingStatus, or array of TrainingStatus
     * @returns Trainings response from the REST API
     */
    listTrainings(modelId, options) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.makeGetRequest(`/models/${modelId}/trainings`, options);
        });
    }
    /**
     * Requests a training, calls the POST /models/{modelId}/trainings endpoint.
     *
     * @param modelId Id of the model
     * @param options.dataBundleIds DataBundle ids that will be used for training
     * @param options.instanceType The type of instance that will be used for training
     * @param options.name Name of the training
     * @param options.description Description of the training
     * @returns Training response from the REST API
     */
    createTraining(modelId, options) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.makePostRequest(`/models/${modelId}/trainings`, options);
        });
    }
    /**
     * Update a training, calls the PATCH /models/{modelId}/trainings/{trainingId} endpoint.
     *
     * @param modelId Id of the model
     * @param trainingId Id of the training
     * @param options.name New name of the training
     * @param options.description New description of the training
     * @param options.status Cancel the training with status = 'cancelled'
     * @returns
     */
    updateTraining(modelId, trainingId, options) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.makePatchRequest(`/models/${modelId}/trainings/${trainingId}`, options);
        });
    }
    /**
     * List payment methods available, calls the GET /paymentMethods endpoint.
     *
     * @param options.maxResults Maximum number of results to be returned
     * @param options.nextToken A unique token for each page, use the returned token to retrieve the next page.
     * @returns PaymentMethods response from the REST API
     */
    listPaymentMethods(options) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.makeGetRequest('/paymentMethods', options);
        });
    }
    /**
     * Creates a payment method, calls the POST /paymentMethods endpoint.
     *
     * @param options.description Optional description of payment method
     * @param options.name Optional name of payment method
     * @returns PaymentMethod response from REST API
     */
    createPaymentMethod(options) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.makePostRequest('/paymentMethods', options);
        });
    }
    /**
     * Get payment method from the REST API, calls the GET /paymentMethods/{paymentMethodId} endpoint.
     *
     * @param paymentMethodId Id of the payment method
     * @returns PaymentMethod response from REST API
     */
    getPaymentMethod(paymentMethodId) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.makeGetRequest(`/paymentMethods/${paymentMethodId}`);
        });
    }
    /**
     * Updates a payment method, calls the PATCH /paymentMethods/{paymentMethodId} endpoint.
     *
     * @param paymentMethodId Id of the payment method
     * @param options.description Optional description of payment method
     * @param options.name Optional name of payment method
     * @param options.stripeSetupIntentSecret Optional input to confirm a payment method has been successfully set up through Stripe
     * @returns PaymentMethod response from REST API
     */
    updatePaymentMethod(paymentMethodId, options) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.makePatchRequest(`/paymentMethods/${paymentMethodId}`, options);
        });
    }
    /**
     * Deletes a payment method, calls the DELETE /paymentMethods/{paymentMethodId} endpoint.
     *
     * @param paymentMethodId Id of the payment method to delete
     * @returns PaymentMethod response from REST API
     */
    deletePaymentMethod(paymentMethodId) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.makeDeleteRequest(`/paymentMethods/${paymentMethodId}`);
        });
    }
    /**
     * List plans available, calls the GET /plans endpoint.
     *
     * @param options.owner Organizations to retrieve plans from
     * @param options.maxResults Maximum number of results to be returned
     * @param options.nextToken A unique token for each page, use the returned token to retrieve the next page.
     * @returns Plans response from the REST API
     */
    listPlans(options) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.makeGetRequest('/plans', options);
        });
    }
    /**
     * Get information about a specific plan, calls the GET /plans/{plan_id} endpoint.
     *
     * @param planId Id of the plan
     * @returns Plan response from the REST API
     */
    getPlan(planId) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.makeGetRequest(`/plans/${planId}`);
        });
    }
    /**
     * Get log, calls the GET /logs/{logId} endpoint.
     *
     * @param logId Id of the log
     * @returns Log response from REST API
     */
    getLog(logId, options) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.makeGetRequest(`/logs/${logId}`, options);
        });
    }
    /**
     * Get profile, calls the GET /profiles/{profileId} endpoint.
     *
     * @param profileId Id of the profile
     * @param options Request options
     * @returns Profile response from REST API
     */
    getProfile(profileId, options) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.makeGetRequest(`/profiles/${profileId}`, options);
        });
    }
    /**
     * Updates a profile, calls the PATCH /profiles/{profileId} endpoint.
     *
     * @param profileId Id of the profile
     * @param options Request options
     * @returns Profile response from REST API
     */
    updateProfile(profileId, options) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.makePatchRequest(`/profiles/${profileId}`, options);
        });
    }
    /**
     * List roles, calls the GET /roles endpoint.
     * @param options Request options
     * @returns Roles response from REST API
     */
    listRoles(options) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.makeGetRequest('/roles', options);
        });
    }
    /**
     * Get role, calls the GET /roles/{roleId} endpoint.
     * @param roleId Id of the role
     * @param options Request options
     * @returns Role response from REST API
     */
    getRole(roleId, options) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.makeGetRequest(`/roles/${roleId}`, options);
        });
    }
    /**
     * Updates a secret, calls the PATCH /secrets/{secretId} endpoint.
     *
     * @param secretId Id of the secret
     * @param data.data Object containing the data you want to keep secret
     * @param data.description Description of the secret
     * @param data.name Name of the secret
     */
    updateSecret(secretId, data) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.makePatchRequest(`/secrets/${secretId}`, data);
        });
    }
    /* eslint-disable @typescript-eslint/no-explicit-any */
    makeGetRequest(path, options = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { requestConfig } = options, query = __rest(options, ["requestConfig"]);
            return this.makeAuthorizedRequest(axios_1.default.get, (0, utils_1.buildURL)(path, query), requestConfig);
        });
    }
    makeDeleteRequest(path, options = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { requestConfig } = options, query = __rest(options, ["requestConfig"]);
            return this.makeAuthorizedRequest(axios_1.default.delete, (0, utils_1.buildURL)(path, query), requestConfig);
        });
    }
    makePostRequest(path, options) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.makeAuthorizedBodyRequest(axios_1.default.post, path, options);
        });
    }
    makePatchRequest(path, options) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.makeAuthorizedBodyRequest(axios_1.default.patch, path, options);
        });
    }
    makeFileServerGetRequest(fileUrl, options = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { requestConfig } = options, query = __rest(options, ["requestConfig"]);
            const constructedRequestConfig = Object.assign({ responseType: 'arraybuffer' }, requestConfig);
            return this.makeAuthorizedFileServerRequest(axios_1.default.get, (0, utils_1.buildURL)(fileUrl, query), constructedRequestConfig);
        });
    }
    makeFileServerPutRequest(fileUrl, content, options = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            options.data = content;
            return this.makeAuthorizedFileServerBodyRequest(axios_1.default.put, fileUrl, options);
        });
    }
    makeAuthorizedFileServerRequest(axiosFn, fileUrl, requestConfig = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const headers = yield this.getAuthorizationHeaders();
            let config = { headers };
            if (requestConfig) {
                config = Object.assign(Object.assign({}, config), requestConfig);
            }
            const result = yield axiosFn(fileUrl, config);
            return result.data;
        });
    }
    makeAuthorizedFileServerBodyRequest(axiosFn, fileUrl, options = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const headers = yield this.getAuthorizationHeaders();
            const { requestConfig, data } = options;
            let config = { headers };
            if (requestConfig) {
                config = Object.assign(Object.assign({}, config), requestConfig);
            }
            const result = yield axiosFn(fileUrl, data, config);
            return result.data;
        });
    }
    makeAuthorizedRequest(axiosFn, path, requestConfig = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const endpoint = `${this.credentials.apiEndpoint}${path}`;
            const headers = yield this.getAuthorizationHeaders();
            let config = { headers };
            if (requestConfig) {
                config = Object.assign(Object.assign({}, config), requestConfig);
            }
            const result = yield axiosFn(endpoint, config);
            return result.data;
        });
    }
    makeAuthorizedBodyRequest(axiosFn, path, options = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const endpoint = `${this.credentials.apiEndpoint}${path}`;
            const headers = yield this.getAuthorizationHeaders();
            const { requestConfig } = options, body = __rest(options, ["requestConfig"]);
            let config = { headers };
            if (requestConfig) {
                config = Object.assign(Object.assign({}, config), requestConfig);
            }
            const result = yield axiosFn(endpoint, body, config);
            return result.data;
        });
    }
    /* eslint-enable */
    getAuthorizationHeaders() {
        return __awaiter(this, void 0, void 0, function* () {
            const accessToken = yield this.credentials.getAccessToken();
            return {
                Authorization: `Bearer ${accessToken}`,
            };
        });
    }
}
exports.Client = Client;
exports.default = Client;
